import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { GetBranchContext, type BranchOption } from "@/api/branchApi";

// App-wide branch (clinic) selection state. A second Branch was created on
// 2026-08-12, so an unrestricted user can no longer have their clinic inferred
// by the backend on insert of a scoped record — they must be asked. This store
// holds the fetched context, drives the one-off selection modal, and caches the
// choice for the session (it only changes when an admin reassigns the user).
//
// The modal is mounted once in App.vue (BranchSelectionModal.vue) and driven
// entirely from here, the same pattern as the QR scanner overlay. Any create
// path can call `chooseBranch()` (via the branchGuard helper) to obtain the
// branch to stamp; the promise resolves when the user confirms and rejects when
// they cancel.

/** Thrown when the user dismisses the branch modal — aborts the pending create. */
export class BranchSelectionCancelledError extends Error {
  constructor() {
    super("Branch selection cancelled");
    this.name = "BranchSelectionCancelledError";
  }
}

export function isBranchSelectionCancelled(error: unknown): boolean {
  return error instanceof BranchSelectionCancelledError;
}

const useBranchStore = defineStore("branch", () => {
  const current = ref<string | null>(null);
  /** The signed-in user's branch warehouse. NOT the till's — see warehouseForBranch. */
  const warehouse = ref<string | null>(null);
  const available = ref<BranchOption[]>([]);
  const seesAllBranches = ref(false);
  const mustSelect = ref(false);
  const loaded = ref(false);

  // The clinic the user picked for this session (null until they choose).
  const chosenBranch = ref<string | null>(null);
  const modalOpen = ref(false);

  const hasContext = computed(() => loaded.value);

  // A single in-flight selection promise, shared so two near-simultaneous
  // creates await the same modal instead of racing two resolvers.
  let pendingResolve: ((branch: string) => void) | null = null;
  let pendingReject: ((error: Error) => void) | null = null;
  let pendingPromise: Promise<string> | null = null;
  // De-dupes concurrent boot/login loads into one request.
  let loadPromise: Promise<void> | null = null;

  async function load(force = false): Promise<void> {
    if (loaded.value && !force)
      return;

    if (loadPromise)
      return loadPromise;

    loadPromise = (async () => {
      const context = await GetBranchContext();

      if (context) {
        current.value = context.current;
        // The signed-in user's own branch warehouse. ⚠️ At a TILL this is the wrong
        // one to use: a till belongs to `POS Profile.branch`, which is not always the
        // user's branch, and for an Administrator `current` is null entirely. Use
        // `warehouseForBranch(POS Profile.branch)` there.
        warehouse.value = context.warehouse;
        available.value = context.available;
        seesAllBranches.value = context.seesAllBranches;
        mustSelect.value = context.mustSelect;

        // A user pinned to one branch has no decision to make; seed the choice
        // so nothing ever prompts them. When must_select is false the guard
        // omits the field entirely anyway, so this is belt-and-suspenders.
        if (!context.mustSelect)
          chosenBranch.value = context.current;
      }

      loaded.value = true;
    })().finally(() => {
      loadPromise = null;
    });

    return loadPromise;
  }

  /**
   * The warehouse a given branch sells from — `Branch.custom_wharehouse`.
   *
   * Authoritative over `POS Profile.warehouse`, which can disagree and does on this
   * site. `null` when the branch is unknown or has no warehouse wired; only then
   * should a caller fall back to the profile's own value.
   */
  function warehouseForBranch(branch: string | null | undefined): string | null {
    const wanted = String(branch ?? "").trim();

    if (!wanted)
      return null;

    const match = available.value.find(option => option.name === wanted || option.branch === wanted);

    return match?.warehouse ?? null;
  }

  /**
   * Resolve the branch to stamp on a scoped insert. Returns the cached choice
   * when present; otherwise opens the modal and waits. Rejects with
   * `BranchSelectionCancelledError` if the user cancels.
   *
   * Callers should only reach here when `mustSelect` is true — the branchGuard
   * short-circuits the not-required cases before calling.
   */
  function chooseBranch(): Promise<string> {
    if (chosenBranch.value)
      return Promise.resolve(chosenBranch.value);

    if (pendingPromise)
      return pendingPromise;

    modalOpen.value = true;
    pendingPromise = new Promise<string>((resolve, reject) => {
      pendingResolve = resolve;
      pendingReject = reject;
    }).finally(() => {
      pendingPromise = null;
      pendingResolve = null;
      pendingReject = null;
    });

    return pendingPromise;
  }

  /** Called by the modal when the user confirms a selection. */
  function confirmBranch(branch: string) {
    const value = String(branch ?? "").trim();
    if (!value)
      return;

    chosenBranch.value = value;
    modalOpen.value = false;
    pendingResolve?.(value);
  }

  /** Called by the modal when the user cancels — aborts every waiting create. */
  function cancelBranch() {
    modalOpen.value = false;
    pendingReject?.(new BranchSelectionCancelledError());
  }

  /** Let the user re-open the picker to change their session choice. */
  function reopen() {
    modalOpen.value = true;
  }

  return {
    current,
    warehouse,
    warehouseForBranch,
    available,
    seesAllBranches,
    mustSelect,
    loaded,
    hasContext,
    chosenBranch,
    modalOpen,
    load,
    chooseBranch,
    confirmBranch,
    cancelBranch,
    reopen,
  };
});

export default useBranchStore;
